import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import { HOME } from "@/constants/testIds";
import { MaskedLines } from "../components/Reveal";

const ease = [0.22, 1, 0.36, 1];

const STATS = [
  { v: "5000+", l: "Amazon Sellers" },
  { v: "2000+", l: "Flipkart Sellers" },
  { v: "2017", l: "Founded, New Delhi" },
];

export default function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
  const tx = useSpring(useTransform(mx, [-0.5, 0.5], [-18, 18]), { stiffness: 80, damping: 20 });
  const ty = useSpring(useTransform(my, [-0.5, 0.5], [-14, 14]), { stiffness: 80, damping: 20 });
  const { scrollY } = useScroll();
  const parallax = useTransform(scrollY, [0, 800], [0, 160]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="relative min-h-[100svh] bg-[#0A0A0A] text-[#F5D5B0] overflow-hidden flex flex-col justify-end"
      data-testid="hero-section"
    >
      {/* grain + spotlight */}
      <div className="absolute inset-0 pointer-events-none grain opacity-[0.07]" />
      <motion.div
        style={{ x: tx, y: ty }}
        className="absolute -top-40 right-[-10%] w-[70vw] h-[70vw] max-w-[900px] max-h-[900px] rounded-full bg-[radial-gradient(circle,rgba(107,20,20,0.75)_0%,rgba(107,20,20,0.15)_40%,transparent_70%)] blur-2xl"
      />
      <span className="absolute top-[18%] -left-6 font-heading font-black text-[22vw] leading-none text-white/[0.025] select-none pointer-events-none tracking-tighter">SELL</span>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full pt-32 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-8">
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8, ease }}
            className="inline-flex items-center gap-3 font-mono text-[11px] tracking-[0.25em] uppercase text-[#C8956A] mb-10"
          >
            <span className="w-8 h-px bg-[#C8956A]" /> Calium Seller Services · Est. 2017 · New Delhi
          </motion.div>

          <h1 data-testid={HOME.heroTitle} className="font-heading font-black tracking-[-0.04em] leading-[0.92] text-[12vw] sm:text-[8vw] lg:text-[5.4vw] text-white">
            <MaskedLines delay={0.35} lines={["We run your", "marketplace."]} />
            <MaskedLines delay={0.6} lines={["You run the brand."]} lineClass="text-[#C8956A]" />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8, ease }}
            className="mt-8 font-body text-base text-[#F5D5B0]/70 max-w-xl leading-relaxed"
          >
            Amazon &amp; Flipkart account management, listing, advertising, account health and reinstatement — handled end-to-end by the team that has scaled 5000+ Indian sellers.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.8, ease }}
            className="mt-8 flex flex-wrap gap-4"
          >
            <a href="#contact" data-testid={HOME.heroCta} className="group inline-flex items-center gap-3 rounded-full bg-[#F5D5B0] text-[#0A0A0A] pl-6 pr-2 py-2 font-body font-bold text-sm transition-transform hover:-translate-y-0.5">
              Get a free account audit
              <span className="w-9 h-9 rounded-full bg-[#6B1414] text-white flex items-center justify-center group-hover:rotate-[-45deg] transition-transform duration-300"><ArrowRight size={16} /></span>
            </a>
            <a href="tel:+917827304742" data-testid="hero-call-link" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3.5 font-body font-semibold text-sm text-white hover:bg-white hover:text-[#0A0A0A] transition-colors">
              <Phone size={14} /> +91 78273 04742
            </a>
          </motion.div>
        </div>

        {/* 3D tilting product frame */}
        <motion.div style={{ y: parallax, opacity: fade }} className="lg:col-span-4 relative hidden md:block" data-testid="hero-visual">
          <motion.div
            style={{ rotateX: rx, rotateY: ry, transformPerspective: 1100 }}
            initial={{ opacity: 0, scale: 0.9, y: 40 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ delay: 0.9, duration: 1.1, ease }}
            className="relative aspect-[4/5] will-change-transform"
          >
            <div className="absolute inset-0 [clip-path:polygon(0_0,100%_6%,94%_100%,6%_94%)] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1607083206968-13611e3d76db?w=1200&q=80"
                alt="Product packaging ready for marketplace dispatch"
                className="w-full h-full object-cover saturate-[0.85]"
              />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_35%_30%,transparent_0%,rgba(10,10,10,0.75)_70%)]" />
            </div>
            <div className="absolute -bottom-6 -left-8 bg-[#6B1414] text-[#F5D5B0] px-5 py-4 rounded-md shadow-2xl border border-white/10" style={{ transform: "translateZ(60px)" }}>
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] opacity-70">Median result</p>
              <p className="font-heading text-2xl font-black">ACoS 34% → 20%</p>
            </div>
            <img src="/logo-mark.png" alt="" aria-hidden="true" className="absolute -top-8 -right-6 w-24 h-24 object-contain drop-shadow-2xl" style={{ transform: "translateZ(90px)" }} />
          </motion.div>
        </motion.div>
      </div>

      {/* stats rail */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 0.8 }}
        className="relative border-t border-white/10"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-3 divide-x divide-white/10" data-testid="hero-stats">
          {STATS.map((s) => (
            <div key={s.l} className="py-6 pl-4 first:pl-0">
              <p className="font-heading text-2xl sm:text-4xl font-black text-white tracking-tight">{s.v}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C8956A] mt-1">{s.l}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
